import { useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Layers,
  Loader2,
  Pencil,
  Receipt,
  ScrollText,
  Share2,
  SkipForward,
  Sparkles,
  Truck,
  Undo2,
  Warehouse,
  Ban,
  Inbox,
  MessageSquarePlus,
  PackagePlus,
  Send,
  XCircle,
} from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/date'
import { money } from '@/lib/money'
import { downloadDesignerExcel } from '@/lib/designerExcel'
import { workedBy } from '@/lib/stageLog'
import StageStrip from '@/components/order/ProductionTimeline'
import { stageRowsFor } from '@/lib/stageRows'
import { ordersListPath } from '@/lib/roles'
import type { DepartmentDeadline, Order, OrderItem, OrderLifecycle } from '@/types'
import Button from '@/components/ui/Button'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import OrderAiCard from '@/components/admin/ai/OrderAiCard'
import TierBadge from '@/components/ui/TierBadge'
import LogoLoader from '@/components/ui/LogoLoader'
import OrderItemsCard from '@/components/order/OrderItemsCard'
import ShipmentModal, { toShipment, type ShipmentDraft } from '@/components/order/ShipmentModal'
import PartnerModal from '@/components/order/PartnerModal'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'
import { useShippingInfo } from '@/lib/addresses'
import { SELF_PICKUP } from '@/lib/pickupHours'
import PickupDetails from '@/components/order/PickupDetails'
import UrgentBadge from '@/components/job/UrgentBadge'
import ShipmentStatusBadge from '@/components/order/ShipmentStatusBadge'
import ShipmentTimeline from '@/components/order/ShipmentTimeline'
import BookShipmentModal from '@/components/order/BookShipmentModal'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { shipmentRefetchInterval } from '@/lib/shipment'
import PaymentBadge, { paymentMethodLabels } from '@/components/order/PaymentBadge'
import OrderHistory from '@/components/order/OrderHistory'
import EditOrderDetailsModal from '@/components/order/EditOrderDetailsModal'
import EditArtworkModal from '@/components/order/EditArtworkModal'
import RescheduleModal from '@/components/order/RescheduleModal'
import CancelOrderModal from '@/components/order/CancelOrderModal'
import RecordPaymentModal from '@/components/order/RecordPaymentModal'
import { PendingRemarkList, PendingRemarkModal, remarksFromEvents } from '@/components/order/PendingRemarks'
import { invalidateOrderViews } from '@/components/order/lifecycle'
import ProceedConfirmDialog from '@/components/order/ProceedConfirmDialog'

/** What the cancelled banner says about the money, from the order's payment state. */
function cancelledMoneyText(order: Order): string {
  switch (order.payment_status) {
    case 'refund_due':
      return `Refund due: return ${money(order.total)} to the customer, then record it with Record refund.`
    case 'refunded':
      return `Refunded ${money(order.refunded_amount)}${order.refunded_at ? ` on ${formatDateTime(order.refunded_at)}` : ''}.`
    case 'paid':
      return 'The payment was kept - no refund.'
    default:
      return 'No payment was taken.'
  }
}

/**
 * One order in full, shared by the admin and sales consoles: items, totals,
 * the production timeline with its per-section deadlines, customer, PIC,
 * payment, delivery, tracking and the history of changes.
 *
 * Sales keeps everything that answers "where is my customer's order", losing
 * the things that change it: the shipment editor, the warehouse hand-off, the
 * job sheet and the lifecycle actions (move, cancel, payment). Those are
 * passed in rather than role-checked here, and none of their routes exist
 * under /sales. Whether an action is possible right now comes from the
 * server's `lifecycle` verdicts, never from a rule recomputed in this page;
 * a disabled button shows the server's reason.
 */
export default function OrderDetailPage({
  basePath,
  can,
}: {
  basePath: '/admin' | '/sales'
  /** `ai`: the MPG AI check card and Ask MPG AI button, admin console only (the assistant is admin only). */
  can: { shipment: boolean; partner: boolean; jobSheet: boolean; lifecycle: boolean; invoice: boolean; ai: boolean }
}) {
  const { id } = useParams()
  const toast = useToast()
  // Where a self pickup order is collected - the current pickup point,
  // since orders do not snapshot it.
  const shippingInfo = useShippingInfo()
  const [shipment, setShipment] = useState<ShipmentDraft | null>(null)
  const [partnerOpen, setPartnerOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  // The item whose artwork is being fixed, or null when the modal is shut.
  const [artworkItem, setArtworkItem] = useState<OrderItem | null>(null)
  // The item whose font is being fixed.
  const [fontItem, setFontItem] = useState<OrderItem | null>(null)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [settle, setSettle] = useState<'payment' | 'refund' | null>(null)
  const [remarkOpen, setRemarkOpen] = useState(false)
  // Back to the list the order was opened from (the Pending tab, say), or
  // the plain list when it was opened from a link or a notification.
  const from = (useLocation().state as { from?: string } | null)?.from
  const backTo = from?.startsWith(ordersListPath(basePath)) ? from : ordersListPath(basePath)
  const [proceedOpen, setProceedOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['order-detail', basePath, id],
    queryFn: async () =>
      (
        await api.get<{ data: Order; department_deadlines: DepartmentDeadline[]; lifecycle?: OrderLifecycle }>(
          `${basePath}/orders/${id}`,
        )
      ).data,
    // While a parcel is moving, webhook-driven changes should show up without
    // a reload. Stops by itself once the shipment is terminal.
    refetchInterval: (query) => shipmentRefetchInterval(query.state.data?.data.shipment),
    refetchIntervalInBackground: false,
  })

  const [booking, setBooking] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const queryClient = useQueryClient()
  const invalidateOrder = () => {
    queryClient.invalidateQueries({ queryKey: ['order-detail'] })
    queryClient.invalidateQueries({ queryKey: ['orders-page'] })
  }

  const refreshLabel = useMutation({
    mutationFn: async () =>
      (await api.post(`${basePath}/orders/${data!.data.id}/shipments/${data!.data.shipment!.id}/label`)).data,
    onSuccess: () => {
      invalidateOrder()
      toast.success('Label fetched')
    },
    onError: (err) => toast.error('Label not ready', errorMessage(err)),
  })

  const voidBooking = useMutation({
    mutationFn: async () =>
      (await api.delete<{ message?: string }>(`${basePath}/orders/${data!.data.id}/shipments/${data!.data.shipment!.id}`)).data,
    onSuccess: (res) => {
      invalidateOrder()
      setVoidOpen(false)
      toast.success('Booking voided', res.message)
    },
    onError: (err) => toast.error('Could not void the booking', errorMessage(err)),
  })

  // Send a pending order to production. The server says who may (sales:
  // orders they took) and whether the dates moved.
  const proceed = useMutation({
    mutationFn: async () => (await api.post<{ message: string }>(`${basePath}/orders/${data!.data.id}/proceed`)).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      setProceedOpen(false)
      toast.success('Proceeded', res.message)
    },
    onError: (err) => toast.error('Could not proceed', errorMessage(err)),
  })

  const order = data?.data
  const deadlines = data?.department_deadlines ?? []
  const lifecycle = data?.lifecycle

  if (isLoading || !order) return <LogoLoader />

  const logs = order.job?.stage_logs ?? []

  // A job can change hands only while it is in production.
  const canHandOver =
    can.partner &&
    Boolean(order.job) && !['pending_payment', 'cancelled'].includes(order.status) && order.job?.status !== 'completed'

  const canEditDetails = Boolean(lifecycle?.edit_details.allowed)
  const canEditPic = can.lifecycle && Boolean(lifecycle?.edit_pic.allowed)
  const canSchedule = can.lifecycle && Boolean(lifecycle?.reschedule.allowed || lifecycle?.replan.allowed)
  // Shown, disabled with the server's reason, while cancelling is still a
  // question worth asking - not on an order that has finished or is gone.
  const showCancel = can.lifecycle && Boolean(lifecycle) && !['cancelled', 'completed'].includes(order.status)

  const copyTrackingLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/track/${order.job!.share_token}`)
    toast.success('Tracking link copied', 'Anyone with this link can view production progress - no sign-in needed.')
  }

  return (
    <div>
      <Link to={backTo} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline">
        <ArrowLeft className="size-4" /> {backTo.includes('tab=pending') ? 'Back to pending' : 'Back to orders'}
      </Link>
      <PageHeader
        title={
          // Beside the number it names, not at the end of the action row,
          // where it wrapped to the edge of the page and was cut off.
          <span className="flex flex-wrap items-center gap-2">
            {order.number}
            <UrgentBadge urgent={order.is_urgent} />
          </span>
        }
        subtitle={`Job ${order.job?.display_code ?? order.job?.code ?? '-'}`}
        actions={
          <>
            {can.jobSheet && order.job && (
              <Link to={`/admin/jobs/${order.job.id}/sheet?from=order`} target="_blank" rel="noreferrer">
                <Button variant="outline">
                  <FileText className="size-4" /> Job sheet
                </Button>
              </Link>
            )}
            {order.job?.share_token && (
              <Button variant="outline" onClick={copyTrackingLink}>
                <Share2 className="size-4" /> Copy tracking link
              </Button>
            )}
            {order.job && order.items?.some((item) => (item.personalizations?.length ?? 0) > 0) && (
              <Button variant="outline" onClick={() => downloadDesignerExcel(order.job!, { ...order, agent: order.agent })}>
                <FileSpreadsheet className="size-4" /> Name list Excel
              </Button>
            )}
            {canHandOver && (
              <Button variant="outline" onClick={() => setPartnerOpen(true)}>
                <Warehouse className="size-4" /> {order.job?.warehouse ? 'Change warehouse partner' : 'Give to warehouse partner'}
              </Button>
            )}
            {(canEditDetails || canEditPic) && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="size-4" /> Edit details
              </Button>
            )}
            {can.ai && !['cancelled', 'completed'].includes(order.status) && (
              // Fills the question in MPG AI; the admin still presses Send.
              <Link
                to={`/admin/ai?q=${encodeURIComponent(
                  `Look at order ${order.number}. When is each section due, is it on track, and what is the best production day for it?`,
                )}`}
              >
                <Button variant="outline">
                  <Sparkles className="size-4" /> Ask MPG AI
                </Button>
              </Link>
            )}
            {canSchedule && (
              <Button variant="outline" onClick={() => setScheduleOpen(true)}>
                <CalendarClock className="size-4" /> {lifecycle?.reschedule.allowed ? 'Move date' : 'Re-plan'}
              </Button>
            )}
            {can.lifecycle && lifecycle?.record_payment.allowed && (
              <Button variant="outline" onClick={() => setSettle('payment')}>
                <Receipt className="size-4" /> Record payment
              </Button>
            )}
            {can.lifecycle && lifecycle?.record_refund.allowed && (
              <Button variant="outline" onClick={() => setSettle('refund')}>
                <Undo2 className="size-4" /> Record refund
              </Button>
            )}
            {showCancel && (
              <Button
                variant="danger"
                disabled={!lifecycle?.cancel.allowed}
                title={lifecycle?.cancel.reason ?? undefined}
                onClick={() => setCancelOpen(true)}
              >
                <XCircle className="size-4" /> Cancel order
              </Button>
            )}
          </>
        }
      />

      {order.status === 'pending_payment' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warning/60 bg-warning/20 p-4 text-sm font-semibold text-warning">
          <AlertTriangle className="size-4" /> Awaiting payment - this order is not in production yet.
        </div>
      )}
      {/* Pending: the office has not sent it to production yet. The floor
          does not see it; Proceed puts it in the first section's queue. */}
      {order.job?.awaiting_proceed && (
        <div className="mb-4 rounded-lg border border-pending/60 bg-pending/20 p-4 text-sm text-pending">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-start gap-2 font-semibold">
              <Inbox className="mt-0.5 size-4 shrink-0" />
              <span>
                Pending - not sent to production yet. {order.job.current_department?.name ?? 'The floor'} does not see it until it
                is proceeded.
                <span className="block text-xs font-normal">
                  {order.job.sheet_printed_at ? `Sheet printed ${formatDateTime(order.job.sheet_printed_at)}.` : 'Sheet not printed yet.'}{' '}
                  Proceeded after its production date, it is re-planned from today.
                </span>
              </span>
            </p>
            {lifecycle && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={!lifecycle.pending_remark.allowed}
                  title={lifecycle.pending_remark.reason ?? undefined}
                  onClick={() => setRemarkOpen(true)}
                >
                  <MessageSquarePlus className="size-4" /> Add remark
                </Button>
                <Button
                  disabled={!lifecycle.proceed.allowed}
                  title={lifecycle.proceed.reason ?? undefined}
                  onClick={() => setProceedOpen(true)}
                >
                  <Send className="size-4" /> Proceed
                </Button>
              </div>
            )}
          </div>
          {remarksFromEvents(order.events).length > 0 && (
            <PendingRemarkList remarks={remarksFromEvents(order.events)} className="mt-3 overflow-x-auto rounded-md bg-white/70 p-2 text-foreground" />
          )}
        </div>
      )}
      {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warning/60 bg-warning/20 p-4 text-sm font-semibold text-warning">
          <AlertTriangle className="size-4 shrink-0" /> Unpaid - produced on credit. {money(order.total)} outstanding.
        </div>
      )}
      {order.status === 'cancelled' && (
        <div
          className={cn(
            'mb-4 rounded-lg border p-4 text-sm',
            order.payment_status === 'refund_due'
              ? 'border-danger/60 bg-danger/20 text-danger'
              : 'border-foreground/30 bg-foreground/10 text-foreground/80',
          )}
        >
          {order.cancelled_by_admin ? (
            <>
              <p className="font-semibold">
                Cancelled{order.cancelled_at ? ` on ${formatDateTime(order.cancelled_at)}` : ''}
                {order.cancel_reason ? `: ${order.cancel_reason}` : ''}
              </p>
              <p className="mt-1">{cancelledMoneyText(order)}</p>
            </>
          ) : (
            'Payment was not completed in time, so this order was cancelled and nothing was charged.'
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <OrderItemsCard
            order={order}
            variant="team"
            editArtwork={{ allowed: canEditDetails, reason: lifecycle?.edit_details.reason, onEdit: setArtworkItem }}
            editFont={{ allowed: canEditDetails, reason: lifecycle?.edit_details.reason, onEdit: setFontItem }}
          />

          {can.ai && !['cancelled', 'completed'].includes(order.status) && <OrderAiCard orderId={order.id} orderNumber={order.number} />}

          <Card>
            <SectionLabel>Production timeline</SectionLabel>
            {order.job?.warehouse && (
              <p className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/60 bg-primary/10 p-2.5 text-xs font-medium text-primary">
                <Warehouse className="size-4" /> This job is produced at external warehouse partner: {order.job.warehouse.name}
                {order.job.outsourced_at && (
                  <span className="font-normal opacity-80">(handed over {formatDateTime(order.job.outsourced_at)})</span>
                )}
              </p>
            )}
            {logs.length === 0 && order.status !== 'cancelled' && (
              <p className="mb-4 rounded-lg bg-app p-2.5 text-xs text-muted">
                Queued for production - the floor team will scan the job QR to begin.
              </p>
            )}
            <ol className="space-y-3">
              {logs.map((log) => (
                <li key={log.id} className="flex items-start gap-3 text-sm">
                  {log.action === 'complete' ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-status-success" />
                  ) : log.action === 'partial' ? (
                    <Layers className="mt-0.5 size-4 shrink-0 text-status-pending" />
                  ) : log.action === 'skip' ? (
                    <SkipForward className="mt-0.5 size-4 shrink-0 text-muted" />
                  ) : log.action === 'issue' ? (
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-status-danger" />
                  ) : (
                    <Loader2 className="mt-0.5 size-4 shrink-0 text-status-processing" />
                  )}
                  <div>
                    <p className="font-semibold">
                      {log.department} -{' '}
                      {log.action === 'start'
                        ? 'started'
                        : log.action === 'complete'
                          ? 'completed'
                          : log.action === 'partial'
                            ? `part done${log.pieces ? ` - ${log.pieces} pcs` : ''}`
                            : log.action === 'skip'
                              ? 'skipped'
                              : 'issue flagged'}
                    </p>
                    <p className="text-xs text-muted">
                      {formatDateTime(log.at)} by {workedBy(log)}
                    </p>
                    {log.reason && <p className="mt-0.5 text-xs font-semibold text-status-danger">{log.reason}</p>}
                  </div>
                </li>
              ))}
            </ol>

            {deadlines.length > 0 && order.status !== 'cancelled' && (
              <div className="mt-5 border-t border-foreground/10 pt-4">
                <StageStrip rows={stageRowsFor(deadlines, order, { team: true })} etd={order.expected_completion_date} />
              </div>
            )}

          </Card>

          {order.events && (
            <Card>
              <SectionLabel>History</SectionLabel>
              <OrderHistory events={order.events} />
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <SectionLabel>Customer</SectionLabel>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-semibold">{order.agent?.name ?? '-'}</p>
                  {order.agent?.company_name && <p className="text-xs text-muted">{order.agent.company_name}</p>}
                </div>
                {order.agent?.tier && <TierBadge name={order.agent.tier} color={order.agent.tier_color} icon={order.agent.tier_icon} />}
              </div>
              {order.agent?.email && (
                <p className="flex justify-between gap-3">
                  <span className="text-muted">Email</span>
                  <span className="break-all text-right font-semibold">{order.agent.email}</span>
                </p>
              )}
              {order.agent?.phone && (
                <p className="flex justify-between">
                  <span className="text-muted">Phone</span>
                  <span className="font-semibold">{order.agent.phone}</span>
                </p>
              )}
              <p className="flex justify-between">
                <span className="text-muted">PIC</span>
                <span className="font-semibold">{order.sales_person ?? '-'}</span>
              </p>
            </div>
          </Card>

          {order.payment_status && (
            <Card>
              <SectionLabel>Payment</SectionLabel>
              <div className="space-y-2 text-sm">
                <p className="flex items-center justify-between gap-2">
                  <span className="text-muted">Status</span>
                  <PaymentBadge status={order.payment_status} />
                </p>
                {order.payment_method && (
                  <p className="flex justify-between">
                    <span className="text-muted">Method</span>
                    <span className="font-semibold">{paymentMethodLabels[order.payment_method]}</span>
                  </p>
                )}
                {order.paid_at && (
                  <p className="flex justify-between">
                    <span className="text-muted">Paid</span>
                    <span className="font-semibold">{formatDateTime(order.paid_at)}</span>
                  </p>
                )}
                {/* Free text typed when the payment was recorded, up to 100
                    characters: on its own line so it wraps instead of squeezing. */}
                {order.payment_reference && (
                  <div className="border-t border-line pt-2">
                    <p className="text-muted">Reference</p>
                    <p className="mt-0.5 break-words font-semibold">{order.payment_reference}</p>
                  </div>
                )}
                {order.payment_status === 'unpaid' && (
                  <p className="flex justify-between">
                    <span className="text-muted">Outstanding</span>
                    <span className="font-semibold text-status-danger">{money(order.total)}</span>
                  </p>
                )}
                {order.refunded_amount != null && (
                  <p className="flex justify-between">
                    <span className="text-muted">Refunded</span>
                    <span className="font-semibold">{money(order.refunded_amount)}</span>
                  </p>
                )}
              </div>
              {/* Not while a gateway payment is still in flight. */}
              {can.invoice && order.status !== 'pending_payment' && (
                <Link to={`${basePath}/orders/${order.id}/invoice`} target="_blank" rel="noreferrer" className="mt-4 block">
                  <Button variant="outline" className="w-full">
                    <ScrollText className="size-4" /> View invoice
                  </Button>
                </Link>
              )}
            </Card>
          )}

          <Card>
            <SectionLabel>Schedule</SectionLabel>
            <div className="space-y-2 text-sm">
              <p className="flex justify-between">
                <span className="text-muted">Ordered</span>
                <span className="font-semibold">{order.created_at ? formatDateTime(order.created_at) : '-'}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Production date</span>
                <span className="font-semibold">{formatDate(order.production_date)}</span>
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Delivery due</span>
                <span className="text-right font-semibold text-brand-600">
                  {formatDate(order.expected_completion_date)}
                  {/* A date picked by hand stays put through re-plans, so the
                      team should know it is a promise, not an estimate. */}
                  {order.delivery_date_set_by_name && (
                    <span className="block text-2xs font-normal text-muted">chosen by {order.delivery_date_set_by_name}</span>
                  )}
                </span>
              </p>
            </div>
          </Card>

          <Card>
            <SectionLabel>Delivery & shipment</SectionLabel>
            <div className="space-y-2 text-sm">
              {order.delivery_method && (
                <p className="flex items-center justify-between">
                  <span className="text-muted">Delivery</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold">
                    <Truck className="size-3.5 text-primary" /> {order.delivery_method}
                  </span>
                </p>
              )}
              {(order.delivery_fee ?? 0) > 0 && (
                <p className="flex justify-between">
                  <span className="text-muted">Delivery fee</span>
                  <span className="font-semibold">{money(order.delivery_fee)}</span>
                </p>
              )}
              {order.delivery_method === SELF_PICKUP && shippingInfo.data && (
                <PickupDetails pickup={shippingInfo.data.pickup} />
              )}
              {order.delivery_address && (
                <div className="rounded-lg bg-app p-2 text-xs">
                  <p className="font-bold">Deliver to</p>
                  <p className="mt-0.5 font-semibold">
                    {order.delivery_address.recipient_name} - {order.delivery_address.phone}
                  </p>
                  <p className="text-muted">
                    {order.delivery_address.line1}
                    {order.delivery_address.line2 ? `, ${order.delivery_address.line2}` : ''},{' '}
                    {order.delivery_address.postcode} {order.delivery_address.city}, {order.delivery_address.state}
                  </p>
                </div>
              )}
              {order.shipment ? (
                <>
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-muted">Courier</span>
                    <span className="font-semibold">{order.shipment.carrier}</span>
                  </p>
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-muted">Courier status</span>
                    <ShipmentStatusBadge status={order.shipment.status} />
                  </p>
                  <p className="flex items-center justify-between gap-2">
                    <span className="shrink-0 text-muted">Tracking no.</span>
                    {order.shipment.tracking_url ?? order.tracking_url ? (
                      <a
                        href={order.shipment.tracking_url ?? order.tracking_url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-w-0 items-center gap-1 break-all font-mono text-xs font-semibold text-brand-600 hover:underline"
                      >
                        {order.shipment.tracking_number} <ExternalLink className="size-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="min-w-0 break-all font-mono text-xs font-semibold">{order.shipment.tracking_number}</span>
                    )}
                  </p>
                  <p className="flex justify-between">
                    <span className="text-muted">Booked</span>
                    <span className="font-semibold">{formatDateTime(order.shipment.booked_at)}</span>
                  </p>
                  {order.shipment.delivered_at ? (
                    <p className="flex justify-between">
                      <span className="text-muted">Delivered</span>
                      <span className="font-semibold text-status-success">{formatDateTime(order.shipment.delivered_at)}</span>
                    </p>
                  ) : order.shipment.last_event_at ? (
                    <p className="flex justify-between">
                      <span className="text-muted">Last update</span>
                      <span className="font-semibold">{formatDateTime(order.shipment.last_event_at)}</span>
                    </p>
                  ) : null}
                  {can.shipment && (
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                      {order.shipment.label_pdf_url ? (
                        <a href={order.shipment.label_pdf_url} target="_blank" rel="noreferrer">
                          <Button variant="outline" className="w-full">
                            <FileText className="size-4" /> Label (PDF)
                          </Button>
                        </a>
                      ) : (
                        <Button variant="outline" className="w-full" loading={refreshLabel.isPending} onClick={() => refreshLabel.mutate()}>
                          <FileText className="size-4" /> Fetch label
                        </Button>
                      )}
                      <Button variant="ghost" title="Void this booking" onClick={() => setVoidOpen(true)}>
                        <Ban className="size-4" /> Void
                      </Button>
                    </div>
                  )}
                  <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-muted">Delivery updates</p>
                  <ShipmentTimeline events={order.shipment.events ?? []} status={order.shipment.status} />
                </>
              ) : (
                <>
                  {order.carrier && (
                    <p className="flex justify-between">
                      <span className="text-muted">Carrier</span>
                      <span className="font-semibold">{order.carrier}</span>
                    </p>
                  )}
                  {order.tracking_number && (
                    <p className="flex items-center justify-between gap-2">
                      <span className="shrink-0 text-muted">Tracking no.</span>
                      {order.tracking_url ? (
                        <a
                          href={order.tracking_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-w-0 items-center gap-1 break-all font-mono text-xs font-semibold text-brand-600 hover:underline"
                        >
                          {order.tracking_number} <ExternalLink className="size-3 shrink-0" />
                        </a>
                      ) : (
                        <span className="min-w-0 break-all font-mono text-xs font-semibold">{order.tracking_number}</span>
                      )}
                    </p>
                  )}
                </>
              )}
              {can.shipment && order.can_book_courier && (
                <Button className="w-full" onClick={() => setBooking(true)}>
                  <PackagePlus className="size-4" /> Book with Pos Laju
                </Button>
              )}
              {can.shipment && (
                <Button variant="outline" className="w-full" onClick={() => setShipment(toShipment(order))}>
                  <Truck className="size-4" /> Edit shipment
                </Button>
              )}
              {order.notes && (
                <p className="rounded-lg bg-app p-2 text-xs">
                  <span className="font-bold">Notes:</span> {order.notes}
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {can.shipment && (
        <ShipmentModal shipment={shipment} onChange={setShipment} onClose={() => setShipment(null)} />
      )}
      {can.shipment && (
        <BookShipmentModal order={booking ? order : null} basePath={basePath} onClose={() => setBooking(false)} />
      )}
      {can.shipment && order.shipment && (
        <ConfirmDialog
          open={voidOpen}
          onClose={() => setVoidOpen(false)}
          onConfirm={() => voidBooking.mutate()}
          title="Void this booking?"
          confirmLabel="Void booking"
          variant="danger"
          loading={voidBooking.isPending}
        >
          <p className="text-sm">
            This clears the booking here so the order can be booked again. It does not cancel anything on the
            courier's side - if the parcel was already dropped off, it will still travel.
          </p>
        </ConfirmDialog>
      )}
      {can.partner && <PartnerModal order={partnerOpen ? order : null} onClose={() => setPartnerOpen(false)} />}
      <EditArtworkModal order={order} item={artworkItem} basePath={basePath} onClose={() => setArtworkItem(null)} />
      <EditArtworkModal order={order} item={fontItem} basePath={basePath} kind="font" onClose={() => setFontItem(null)} />
      <ProceedConfirmDialog
        order={
          proceedOpen
            ? { number: order.number, production_date: order.production_date, section: order.job?.current_department?.name }
            : null
        }
        loading={proceed.isPending}
        onClose={() => setProceedOpen(false)}
        onConfirm={() => proceed.mutate()}
      />
      <PendingRemarkModal
        basePath={basePath}
        order={remarkOpen ? order : null}
        remarks={remarksFromEvents(order.events)}
        onClose={() => setRemarkOpen(false)}
      />
      <EditOrderDetailsModal
        order={editOpen ? order : null}
        basePath={basePath}
        canEditDetails={canEditDetails}
        detailsReason={lifecycle?.edit_details.reason}
        canEditPic={canEditPic}
        onClose={() => setEditOpen(false)}
      />
      {can.lifecycle && (
        <>
          <RescheduleModal order={scheduleOpen ? order : null} lifecycle={lifecycle} onClose={() => setScheduleOpen(false)} />
          <CancelOrderModal order={cancelOpen ? order : null} refund={lifecycle?.refund} onClose={() => setCancelOpen(false)} />
          <RecordPaymentModal order={settle ? order : null} mode={settle ?? 'payment'} onClose={() => setSettle(null)} />
        </>
      )}
    </div>
  )
}
